import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * What Algebra I opens with, before it starts reshaping expressions.
 *
 * Three of these four finish the linear thread rather than beginning a new
 * one. A student arriving from Grade 8 can already solve a multi-step equation
 * and read a slope; what they have not met is an equation whose denominators
 * must go first, two conditions imposed at once, or the fact that one line has
 * three standard spellings and each was invented to make a different fact
 * readable. All three are ordinary in Algebra I and none of them existed in
 * the graph before.
 *
 * `real-numbers` is the odd one out and comes first on purpose. Every
 * rearrangement in the course ahead — collecting like terms, expanding a
 * bracket, moving a term across an equals sign — is licensed by a property
 * with a name, and a student who has never seen the list has been using the
 * rules by imitation. Naming them is also the first time the *sets* of number
 * met since Grade 6 are drawn as one nested picture, which is what makes the
 * irrationals look like a family rather than an exception.
 */
export const ALGEBRA_ONE_LINEAR_LESSONS: Lesson[] = [
  lesson({
    id: 'real-numbers-01',
    unitId: 'unit-real-numbers',
    conceptId: 'real-numbers',
    title: 'The Real Numbers',
    summary:
      'The nested families of number met so far, and the handful of rules that licence every rearrangement in the course ahead.',
    objectives: [
      'Classify a number into every set it belongs to',
      'Name the property that justifies a rearrangement',
      'Explain why subtraction and division get no commutative property',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Every number you have met',
        blocks: [
          text('Six years of mathematics have added one kind of number at a time: the counting numbers, then zero, then the negatives, then the fractions, and finally — with square roots — the numbers that are not fractions at all.'),
          text('Put together, that collection is the **real numbers**, and it is everything you can mark on a number line. This lesson draws the families as one picture and names the rules they obey.'),
        ],
      },
      intuition: {
        title: 'The families, and the rules',
        blocks: [
          text('The sets nest. Every natural number is an integer, every integer is rational, and every rational number is real — so a number belongs to several families at once, and "which set is it in" almost always has more than one right answer.'),
          table(['Set', 'What it holds', 'Examples'], [
            ['Natural', 'the counting numbers', '1, 2, 3'],
            ['Whole', 'the naturals and zero', '0, 1, 2'],
            ['Integers', 'the whole numbers and their negatives', '−3, 0, 7'],
            ['Rational', 'anything writable as one integer over another', '½, −4, 0.75, 0.3̅'],
            ['Irrational', 'the reals that no fraction equals', '√2, π'],
            ['Real', 'every point on the number line', 'all of the above'],
          ]),
          warning('A decimal is not automatically irrational. 0.75 is ¾ and 0.333… is ⅓ — both rational. It takes a decimal that never ends *and never repeats* to escape the fractions, which is why √2 and π are the usual examples and why they are hard to come by.', 'Which decimals escape'),
          insight('Rational literally means *ratio*. The word is not describing how sensible the number is; it is saying the number is a ratio of two integers. Irrational numbers are not unreasonable, merely un-fraction-able.'),
          text('Now the rules. Each is a fact about arithmetic so familiar that it looks like nothing, and each is the licence for a move you will make hundreds of times this year.'),
          table(['Property', 'What it says', 'What it licenses'], [
            ['Commutative', 'a + b = b + a, ab = ba', 'reordering terms or factors'],
            ['Associative', '(a + b) + c = a + (b + c)', 'regrouping without brackets'],
            ['Distributive', 'a(b + c) = ab + ac', 'expanding and factoring'],
            ['Identity', 'a + 0 = a, a × 1 = a', 'adding zero or scaling by one to no effect'],
            ['Inverse', 'a + (−a) = 0, a × ¹⁄ₐ = 1', 'undoing an operation — how equations get solved'],
          ]),
          insight('The distributive property is the load-bearing one. Expanding a bracket is it read left to right; factoring is the same statement read right to left. The whole of the polynomial unit is one property applied carefully.'),
          warning('Subtraction and division get no commutative or associative property, and this is not an oversight. 7 − 3 is not 3 − 7, and (12 ÷ 6) ÷ 2 is not 12 ÷ (6 ÷ 2). Reordering a subtraction is one of the commonest wrong moves in algebra, and no rule permits it.', 'The two that are missing'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'number-line',
              from: -3,
              to: 3,
              step: 1,
              marks: [
                { at: -2, label: '−2' },
                { at: 0, label: '0' },
                { at: 1.414, label: '√2' },
                { at: 2.5, label: '5/2' },
              ],
            },
            'Four reals on one line: an integer, zero, an irrational and a fraction. Nothing distinguishes them by eye — √2 does not sit in a gap or look unusual, which is exactly the point.',
          ),
          text('The irrationals are not rare curiosities dotted between the fractions. Between any two fractions there are infinitely many irrationals, and the line is far more theirs than ours.'),
          tip('To decide whether a square root is irrational, ask whether the number under it is a perfect square. √49 is 7 and rational; √50 is irrational. That single check settles most classification questions you will be asked.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Name every set that −6, √16 and √11 belong to. Then name the property used in each step of 3(x + 4) + 2x = 5x + 12.'),
          steps([
            { label: '−6 is negative but whole.', latex: '-6: \\text{ integer, rational, real}' },
            { label: '√16 is 4, so it was never irrational.', latex: '\\sqrt{16} = 4: \\text{ natural, whole, integer, rational, real}' },
            { label: '11 is not a perfect square.', latex: '\\sqrt{11}: \\text{ irrational, real}' },
            { label: 'Expand the bracket — distributive.', latex: '3x + 12 + 2x' },
            { label: 'Reorder to bring like terms together — commutative.', latex: '3x + 2x + 12' },
            { label: 'Collect them — distributive again, backwards.', latex: '(3 + 2)x + 12 = 5x + 12' },
          ]),
          text('Note the third step. Collecting like terms is usually taught as its own rule, but it is the distributive property read from right to left — 3x + 2x is (3 + 2)x, and that is why only *like* terms collect. 3x + 2y has no common factor to take out.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('real-numbers', 4)] },
      independent: { title: 'On your own', blocks: [practice('real-numbers', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('real-numbers', 3, 'Roots that turn out rational, and naming the property behind a step.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The sets nest: every natural is an integer, every integer is rational, every rational is real.',
            'Rational means "a ratio of two integers" — nothing to do with being sensible.',
            'A decimal that ends or repeats is rational; only a non-repeating endless one is not.',
            '√n is irrational unless n is a perfect square.',
            'Commutative reorders, associative regroups, distributive expands and factors.',
            'Subtraction and division are neither commutative nor associative.',
            'Collecting like terms is the distributive property run backwards.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'equations-with-fractions-01',
    unitId: 'unit-linear-forms',
    conceptId: 'equations-with-fractions',
    title: 'Equations with Fractions',
    summary: 'Clearing every denominator in one move, so an equation with fractions becomes one without.',
    objectives: [
      'Find the lowest common denominator of an equation',
      'Multiply every term by it to clear the fractions',
      'Solve the whole-number equation that results',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Make the fractions go away',
        blocks: [
          text('Here is an equation nobody wants to solve as it stands:'),
          math('\\frac{x}{3} + \\frac{x}{4} = 14'),
          text('You could find a common denominator, add the two fractions and divide. It works. There is a faster route that removes the fractions from the problem entirely, before any solving begins.'),
          insight('Multiply *both sides* by a number every denominator divides into. Every fraction cancels, and what is left is an equation of the kind you have solved for two years.'),
        ],
      },
      intuition: {
        title: 'One multiplication, and the trap in it',
        blocks: [
          text('The number to multiply by is the **lowest common denominator** — the smallest number all the denominators divide into. For thirds and quarters that is 12.'),
          math('12 \\times \\frac{x}{3} + 12 \\times \\frac{x}{4} = 12 \\times 14 \\;\\longrightarrow\\; 4x + 3x = 168'),
          text('From there it is one step: 7x = 168, so x = 24. The fractions never had to be added.'),
          warning('Every term gets multiplied, including the ones that are not fractions. Multiplying only the fractions and leaving the 14 alone gives 4x + 3x = 14, which is a different equation with a different answer. This is the error that makes the method feel unreliable, and it is the only real difficulty in the topic.', 'Every term, without exception'),
          insight('Why it is legal: multiplying both sides of an equation by the same non-zero number leaves it true, and the distributive property carries that multiplication onto each term. Nothing new is being used — the licence was in the last lesson.'),
          text('A denominator holding more than one term needs a bracket when it is cleared, because the whole numerator was over the denominator:'),
          math('\\frac{x + 2}{5} \\;\\longrightarrow\\; 5 \\times \\frac{x + 2}{5} = x + 2'),
          warning('Write the bracket. Clearing (x + 2)/5 by multiplying by 5 gives x + 2, not x + 2/5 — and if a minus sign sits in front of the fraction, dropping the bracket flips the sign of only half of what follows.', 'Keep the numerator together'),
          tip('Check the answer in the *original* equation, fractions and all. That is the only version guaranteed to be the question you were asked; an arithmetic slip while clearing produces an equation that is perfectly solvable and simply not the right one.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Equation', 'LCD', 'After clearing'], [
            ['x/2 + x/3 = 5', '6', '3x + 2x = 30'],
            ['x/4 − 1 = 2', '4', 'x − 4 = 8'],
            ['(x + 1)/3 = 6', '3', 'x + 1 = 18'],
            ['2x/5 = (x − 3)/2', '10', '4x = 5(x − 3)'],
          ]),
          text('Every row on the right is an ordinary linear equation. The whole method exists to get from the left column to the right one in a single step, and the third and fourth rows are where the brackets earn their keep.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve (x + 3)/2 − x/6 = 4.'),
          steps([
            { label: 'The denominators are 2 and 6, so the LCD is 6.', latex: '\\text{LCD} = 6' },
            { label: 'Multiply every term by 6 — including the 4.', latex: '6 \\cdot \\frac{x + 3}{2} - 6 \\cdot \\frac{x}{6} = 6 \\cdot 4' },
            { label: 'Cancel. The first numerator keeps its bracket.', latex: '3(x + 3) - x = 24' },
            { label: 'Expand.', latex: '3x + 9 - x = 24' },
            { label: 'Collect and solve.', latex: '2x = 15 \\;\\Rightarrow\\; x = 7.5' },
          ]),
          text('Check in the original: (7.5 + 3)/2 = 5.25, and 7.5/6 = 1.25, and 5.25 − 1.25 = 4. ✓'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('equations-with-fractions', 4)] },
      independent: { title: 'On your own', blocks: [practice('equations-with-fractions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('equations-with-fractions', 3, 'Numerators with more than one term, where the bracket is the whole difficulty.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply both sides by the lowest common denominator to clear every fraction at once.',
            'Every term is multiplied, including whole numbers with no denominator.',
            'A numerator of more than one term keeps a bracket when its denominator cancels.',
            'What remains is an ordinary linear equation.',
            'Check in the original equation, not the cleared one.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'compound-inequalities-01',
    unitId: 'unit-linear-forms',
    conceptId: 'compound-inequalities',
    title: 'Compound Inequalities',
    summary: 'Two conditions at once — and the whole difference between wanting both of them and wanting either.',
    objectives: [
      'Tell an "and" inequality from an "or" inequality',
      'Solve a three-part inequality by operating on all three parts',
      'Graph the solution and write it correctly',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Two conditions',
        blocks: [
          text('A ride requires you to be taller than 120 cm **and** shorter than 195 cm. A discount applies if you are under 16 **or** over 65.'),
          text('Both statements impose two conditions. They are not the same kind of statement, and the difference between them is the entire lesson.'),
          math('120 < h < 195 \\qquad\\text{versus}\\qquad a < 16 \\;\\text{ or }\\; a > 65'),
        ],
      },
      intuition: {
        title: 'And narrows, or widens',
        blocks: [
          text('An **and** inequality needs both conditions true at once, so its solution is the *overlap* of the two — a single stretch between two bounds, and it can easily be empty.'),
          text('An **or** inequality needs only one of them, so its solution is *everything either condition allows* — usually two separate pieces heading off in opposite directions.'),
          insight('One is an intersection and the other is a union, and they behave oppositely: adding a condition to an "and" can only shrink the answer, while adding one to an "or" can only grow it.'),
          table(['Written', 'Means', 'Solution looks like'], [
            ['3 < x < 8', 'x > 3 and x < 8', 'one stretch, between the bounds'],
            ['x < 3 or x > 8', 'either one', 'two pieces, outside the bounds'],
            ['x > 8 and x < 3', 'both — impossible', 'no solution at all'],
            ['x < 8 or x > 3', 'either — always true', 'every real number'],
          ]),
          warning('Only an "and" may be written in three-part form. "x < 3 or x > 8" written as 8 < x < 3 says x is simultaneously above 8 and below 3, which is nothing — and it is the classic way to turn a perfectly good two-piece answer into an empty one.', 'Three parts means "and"'),
          text('Solving a three-part inequality is solving an ordinary one with a third part carried along. Whatever you do, do it to all three:'),
          math('-1 < 2x + 5 < 11 \\;\\longrightarrow\\; -6 < 2x < 6 \\;\\longrightarrow\\; -3 < x < 3'),
          warning('The negative rule still applies, and now it reverses *both* signs. Dividing −6 < −2x < 4 by −2 gives 3 > x > −2, which is tidier written −2 < x < 3. Turning one sign and not the other produces a statement that is not merely wrong but meaningless.', 'Both signs turn'),
          text('An "or" is not solved in one line, because its two halves have nothing to do with each other. Solve each separately, then report both.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -6, to: 6, step: 1, marks: [{ at: -3, label: '−3' }, { at: 3, label: '3' }] },
            'The "and" solution −3 < x < 3: one connected stretch between the two bounds, with neither endpoint included.',
          ),
          figure(
            { kind: 'number-line', from: -6, to: 6, step: 1, ray: { at: 3, direction: 'right', closed: false } },
            'Half of an "or" solution. The other half is a matching ray heading left from −3, and the two never meet — which is what makes it two pieces rather than one.',
          ),
          tip('Draw it before writing it. An "and" that comes out as two disconnected rays means the conditions have no overlap and the answer is "no solution"; an "or" that covers the whole line means every number qualifies. The picture catches both, and the algebra alone does not.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve −4 ≤ 3x − 1 < 8, then solve 2x + 1 < 3 or x − 4 > 1.'),
          steps([
            { label: 'Add 1 to all three parts.', latex: '-3 \\le 3x < 9' },
            { label: 'Divide all three by 3 — positive, so nothing turns.', latex: '-1 \\le x < 3' },
            { label: 'For the "or", solve the left half alone.', latex: '2x < 2 \\;\\Rightarrow\\; x < 1' },
            { label: 'Then the right half alone.', latex: 'x > 5' },
            { label: 'Report both pieces.', latex: 'x < 1 \\;\\text{ or }\\; x > 5' },
          ]),
          text('The first answer includes −1 and excludes 3, because the inequality signs were different at the two ends. The second cannot be compressed into three-part form — there is no single stretch to describe.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('compound-inequalities', 4)] },
      independent: { title: 'On your own', blocks: [practice('compound-inequalities', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('compound-inequalities', 3, 'Negative coefficients, and deciding whether a compound statement has any solutions at all.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '"And" wants both conditions: the overlap, one stretch, possibly empty.',
            '"Or" wants either: the union, usually two pieces.',
            'Three-part form is only ever an "and".',
            'Operate on all three parts, and reverse both signs when dividing by a negative.',
            'Solve the two halves of an "or" separately.',
            'Graph it to catch an empty answer or an "every number" answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-equation-forms-01',
    unitId: 'unit-linear-forms',
    conceptId: 'linear-equation-forms',
    title: 'Forms of a Linear Equation',
    summary: 'The same line written three ways, and the fact each way was designed to make obvious.',
    objectives: [
      'Write a line in point-slope form from a point and a slope',
      'Convert between point-slope, slope-intercept and standard form',
      'Choose the form that suits what you were given',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'One line, three spellings',
        blocks: [
          text('These three equations describe exactly the same line:'),
          math('y - 1 = 2(x - 3) \\qquad y = 2x - 5 \\qquad 2x - y = 5'),
          text('Not similar lines — the same one, passing through the same points. They differ only in which fact about the line is readable without doing any work.'),
        ],
      },
      intuition: {
        title: 'What each form was built to show',
        blocks: [
          text('A line is fixed by very little: a slope and any one point on it. Each form is that information arranged to expose something different.'),
          table(['Form', 'Written', 'Reads off at a glance'], [
            ['Point-slope', 'y − y₁ = m(x − x₁)', 'a point (x₁, y₁), and the slope'],
            ['Slope-intercept', 'y = mx + c', 'the slope, and where it crosses the y-axis'],
            ['Standard', 'Ax + By = C', 'both intercepts, easily'],
          ]),
          insight('**Point-slope is the one to write first.** Given a point and a slope — which is what a problem almost always hands you — it needs no rearranging at all: substitute and you are done. Slope-intercept requires finding c first, which is an extra step and an extra chance to err.'),
          math('m = 2,\\ (3, 1) \\;\\longrightarrow\\; y - 1 = 2(x - 3)'),
          warning('The signs in point-slope are subtractions, so a negative coordinate produces a double negative that must be kept. The point (−4, 2) with slope 3 gives y − 2 = 3(x + 4), because x − (−4) is x + 4. Writing x − 4 there puts the line four units off, and it is the commonest error in the topic.', 'Subtracting a negative'),
          text('Converting is only ever rearranging. Point-slope to slope-intercept: expand and make y the subject. Slope-intercept to standard: move the x term across and, by convention, clear fractions and leave A positive.'),
          insight('Standard form earns its place on the intercepts. Set y = 0 and read x; set x = 0 and read y. For 2x − y = 5 that is x = 2.5 and y = −5, in two seconds and no rearranging — which is why it is the form of choice for sketching quickly and for the systems work ahead.'),
          text('Standard form also handles the one line the others cannot. A vertical line has no slope, so it has no point-slope and no slope-intercept form at all; as Ax + By = C it is simply x = 4.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 8,
              lines: [{ slope: 2, intercept: -5, label: 'the line' }],
              points: [
                { x: 3, y: 1, label: '(3, 1)' },
                { x: 0, y: -5, label: '(0, −5)' },
                { x: 2.5, y: 0, label: '(2.5, 0)' },
              ],
            },
            'One line, three marked points. Point-slope was built around the first, slope-intercept around the second, standard form around the second and third together.',
          ),
          tip('Given two points rather than a point and a slope, the work is the same with one step in front: find the slope from the pair, then use *either* point in point-slope form. Both choices give the same line, so pick whichever has the tidier numbers.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A line passes through (−2, 7) and (4, −5). Write it in all three forms.'),
          steps([
            { label: 'Find the slope.', latex: 'm = \\frac{-5 - 7}{4 - (-2)} = \\frac{-12}{6} = -2' },
            { label: 'Point-slope, using (4, −5).', latex: 'y + 5 = -2(x - 4)' },
            { label: 'Expand for slope-intercept.', latex: 'y + 5 = -2x + 8 \\;\\Rightarrow\\; y = -2x + 3' },
            { label: 'Move the x term across for standard form.', latex: '2x + y = 3' },
          ]),
          text('Check with the other point: 2(−2) + 7 = 3. ✓ Using (−2, 7) in step two would have given y − 7 = −2(x + 2), which expands to the same y = −2x + 3 — a different-looking start and an identical line.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-equation-forms', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-equation-forms', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-equation-forms', 3, 'Negative coordinates in point-slope, and reading intercepts off standard form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A slope and any one point fix a line completely.',
            'Point-slope, y − y₁ = m(x − x₁), needs no rearranging when that is what you were given.',
            'A negative coordinate makes a double negative: (−4, 2) gives x + 4.',
            'Slope-intercept shows the slope and the y-intercept; standard form shows both intercepts.',
            'Converting between them is rearranging, never recalculating.',
            'A vertical line has only a standard form.',
          ]),
        ],
      },
    },
  }),
];
